/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import { useConfigStore } from '@/stores';
import type { TFunction } from 'i18next';

export const useFacilityBuildingOptions = (t: TFunction<'translation', undefined>) => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const language = useConfigStore((state) => state.config.ui.language);

    return useMemo(() => {
        const allBuildingOption = {
            label: t('shop.search.text.content.all_building'),
            value: 'all',
        };

        const listBuildings =
            buildings?.map((building) => ({
                label: building.name[language] || building.name['ko'] || '',
                value: building.id,
            })) || [];

        return buildings?.length && buildings.length > 1 ? [allBuildingOption, ...listBuildings] : listBuildings;
    }, [language, buildings]);
};

export const useFacilityFloorList = (selectedBuildingId: string | null, t: TFunction) => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const language = useConfigStore((state) => state.config.ui.language);

    return useMemo(() => {
        if (!buildings) {
            const allFloorOption = {
                id: 'all',
                label: t('shop.search.text.content.all_floor'),
                value: 'all',
                data: null,
            };
            return [allFloorOption];
        }

        // Get all floor codes for "all" option based on selected building
        let allFloorCodes: string[] = [];
        if (selectedBuildingId === 'all') {
            // Get all floor codes from all buildings
            buildings.forEach((building) => {
                if (building.filterPoiContents.facility?.length) {
                    const floorCodes = building.filterPoiContents.facility.map((poi) => poi.studioFloorId);
                    allFloorCodes.push(...floorCodes);
                }
            });
            allFloorCodes = [...new Set(allFloorCodes)]; // Remove duplicates
        } else {
            // Get floor codes from specific building
            const building = buildings.find((b) => b.id === selectedBuildingId) || buildings[0];
            if (building?.filterPoiContents.facility?.length) {
                allFloorCodes = building.filterPoiContents.facility.map((poi) => poi.studioFloorId);
                allFloorCodes = [...new Set(allFloorCodes)]; // Remove duplicates
            }
        }

        const allFloorOption = {
            id: 'all',
            label: t('shop.search.text.content.all_floor'),
            value: 'all',
            data: allFloorCodes,
        };

        // === If "all buildings" is selected ===
        if (selectedBuildingId === 'all') {
            // Get all floors that have facilityContents, using a more efficient approach
            const floorMap = new Map<string, string[]>();

            buildings.forEach((building) => {
                if (!building.filterPoiContents.facility?.length) return;

                const floorCodesWithContent = new Set(building.filterPoiContents.facility.map((poi) => poi.studioFloorId));

                building.floors?.forEach((floor) => {
                    if (floorCodesWithContent.has(floor.floorCode)) {
                        const label = floor.name[language] || floor.name['ko'];
                        const existingCodes = floorMap.get(label) || [];
                        if (!existingCodes.includes(floor.floorCode)) {
                            floorMap.set(label, [...existingCodes, floor.floorCode]);
                        }
                    }
                });
            });

            const mergedFloors = Array.from(floorMap.entries()).map(([label, floorCodes]) => ({
                id: label,
                label,
                value: label,
                data: floorCodes,
            }));

            if (mergedFloors.length === 0) return [allFloorOption];

            // Sort floors efficiently
            mergedFloors.sort((a, b) => {
                const aMatch = a.label.match(/(B?)(\d+)/i);
                const bMatch = b.label.match(/(B?)(\d+)/i);

                if (!aMatch || !bMatch) return a.label.localeCompare(b.label);

                const aNum = aMatch[1].toUpperCase() === 'B' ? -parseInt(aMatch[2], 10) : parseInt(aMatch[2], 10);
                const bNum = bMatch[1].toUpperCase() === 'B' ? -parseInt(bMatch[2], 10) : parseInt(bMatch[2], 10);

                return aNum - bNum;
            });

            return [allFloorOption, ...mergedFloors];
        }

        // === If a specific building is selected ===
        const buildingId = selectedBuildingId || buildings[0]?.id;
        if (!buildingId) return [allFloorOption];

        const building = buildings.find((b) => b.id === buildingId);
        if (!building || !building.filterPoiContents.facility?.length) return [allFloorOption];

        const floorCodesWithContent = new Set(building.filterPoiContents.facility.map((poi) => poi.studioFloorId));

        const floors =
            building.floors
                ?.filter((floor) => floorCodesWithContent.has(floor.floorCode))
                .map((floor) => ({
                    id: floor.id,
                    label: floor.name[language] || floor.name['ko'],
                    value: floor.id,
                    data: floor,
                }))
                .sort((a, b) => {
                    const aMatch = a.label.match(/(B?)(\d+)/i);
                    const bMatch = b.label.match(/(B?)(\d+)/i);

                    if (!aMatch || !bMatch) return a.label.localeCompare(b.label);

                    const aNum = aMatch[1].toUpperCase() === 'B' ? -parseInt(aMatch[2], 10) : parseInt(aMatch[2], 10);
                    const bNum = bMatch[1].toUpperCase() === 'B' ? -parseInt(bMatch[2], 10) : parseInt(bMatch[2], 10);

                    return aNum - bNum;
                }) || [];

        return floors.length > 0 ? [allFloorOption, ...floors] : [allFloorOption];
    }, [language, buildings, selectedBuildingId]);
};

export const useFacilityCategories = (selectedBuildingId: string) => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const mergedCategories = useConfigStore((state) => state.config.mergedCategories.facility);

    if (selectedBuildingId === 'all') {
        return mergedCategories;
    }

    const building = buildings?.find((b) => b.id === selectedBuildingId);
    if (!building) return [];

    return building.filterCategories.facility || [];
};
